// Third-party libraries
import React, { useState, useEffect, useContext, useMemo } from "react";

import { Link as DomLink, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import HelpIcon from "@mui/icons-material/Help";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewSharpIcon from "@mui/icons-material/OpenInNewSharp";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingTwoToneIcon from "@mui/icons-material/LocalShippingTwoTone";
import {
  Grid,
  Box,
  Container,
  Checkbox,
  TextField,
  FormControlLabel,
  Stack,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Backdrop,
  Button,
  Tooltip,
  IconButton,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Chip,
} from "@mui/material";

// Local modules
import { CartContext } from "../../utils/stateHandlers/contexts";
import api from "../../utils/api";
import storage from "../../utils/storageFactory";
import formatting from "../../utils/formatHelpers";
import StringConstants from "../../utils/constants";
import MediaPanel from "./controls/MediaPanel";
import DiamondIconStrip from "./controls/DiamondIconStrip";
import RecentlyViewedDiamondLink from "./controls/RecentlyViewedDiamondLink";
import Modal from "./controls/Modal";
import useMediaQueryUtils from "../../utils/mediaQueryUtils";

export default function CertDiamondDetails(props) {
  const { cartDispatch } = useContext(CartContext);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [diamond, setDiamond] = useState({});
  const [fields, setFields] = useState({});
  const [conditionalSale, setConditionalSale] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [rvCounter, setRvCounter] = useState(0);
  const [showOFCHelp, setShowOFCHelp] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [terms, setTerms] = useState(false);
  const [diamondUnavailable, setDiamondUnavailable] = useState(false);
  const isMdScreen = useMediaQueryUtils("md");

  const mined = StringConstants.DIAMOND_MINED_OR_LAB_MINED_DESC;
  const lab = StringConstants.DIAMOND_MINED_OR_LAB_LAB_DESC;
  const loggedIn = storage.getStorageItem("authToken") ? true : false;
  const sendTo = useLocation();

  useEffect(() => {
    let isMounted = true;
    setPageLoaded(false);

    const loadDiamondData = async () => {
      try {
        const [diamondResponse, fieldsResponse] = await Promise.all([
          api.getRequest(`diamonds/detail/${props.match.params.HooverStockId}`),
          api.getRequest("diamonds/fields"),
        ]);

        if (!isMounted) {
          return;
        }

        const diamondData = diamondResponse.data;
        setDiamond(diamondData);
        setDiamondUnavailable(false);

        setFields(fieldsResponse.data);
        setPageLoaded(true);
      } catch (error) {
        setDiamond({});
        setDiamondUnavailable(true);
        if (!isMounted) {
          return;
        }

        toast.error("We couldn't load the diamond details. Please try again.");
        setPageLoaded(true);
      }
    };

    loadDiamondData();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.match.params.HooverStockId]);

  const getDisplayValue = (value, fallback = "None") => {
    if (value === null || value === undefined) {
      return fallback;
    }

    if (typeof value === "string" && value.trim() === "") {
      return fallback;
    }

    return value;
  };

  const formatWithFallback = (formatKey, value) => {
    const formatFn = formatting[formatKey];

    if (!formatFn) {
      return value;
    }

    if (value === null || value === undefined) {
      return value;
    }

    return formatFn(value);
  };

  const formatHeadlineText = () => {
    const caratValue = getDisplayValue(diamond.Carat);
    const shapeValue = getDisplayValue(diamond.Shape);
    const caratText = caratValue === "None" ? "None" : `${caratValue}ct`;
    const shapeText = shapeValue === "None" ? "None" : `${shapeValue} Shape`;

    if (caratText === "None" && shapeText === "None") {
      return "None";
    }

    return `${caratText} ${shapeText}`.trim();
  };

  const buildDetailLine = () => {
    const caratValue = getDisplayValue(diamond.Carat);
    const shapeValue = getDisplayValue(diamond.Shape);
    const colorValue = getDisplayValue(diamond.Color || diamond.FancyColor);
    const clarityValue = getDisplayValue(diamond.Clarity);
    const certificationValue = getDisplayValue(diamond.HooverCertification);
    const caratText = caratValue === "None" ? "None" : `${caratValue}ct`;
    const certificationText =
      certificationValue === "None"
        ? "None"
        : `${certificationValue} Certified`;

    const parts = [
      caratText,
      shapeValue,
      colorValue,
      clarityValue,
      certificationText,
    ]
      .filter((part) => part !== undefined && part !== null)
      .map((part) => (typeof part === "string" ? part.trim() : part))
      .filter((part) => part !== "");

    if (!parts.some((part) => part !== "None")) {
      return "None";
    }

    return parts.join(" ");
  };

  const resolveFieldValue = (field) => {
    if (!field) {
      return null;
    }

    if (field.name === "LWRatio" || field.label === "L/W Ratio") {
      return (
        diamond?.[field.name] ?? diamond?.LWRatio ?? diamond?.["L/W Ratio"]
      );
    }

    return diamond?.[field.name];
  };

  const skuDisplay = getDisplayValue(diamond.HooverStockId);
  const pricePerCaratDisplay = getDisplayValue(
    formatWithFallback("price2", diamond.CalcPricePerCarat)
  );
  const percentOffRapDisplay = getDisplayValue(
    formatWithFallback("percent0", diamond.CalcPercentOffRap)
  );
  const totalPriceDisplay = getDisplayValue(
    formatWithFallback("price2", diamond.HooverPrice)
  );

  const recentlyViewedData = JSON.parse(
    storage.getSessionStorageItem("hsRecentlyViewedData")
  );

  let rvPics = "";

  if (recentlyViewedData && recentlyViewedData.length > 2) {
    rvPics = (
      <>
        <RecentlyViewedDiamondLink
          key={recentlyViewedData[rvCounter + 0].HooverStockId}
          rv={recentlyViewedData[rvCounter + 0]}
        />
        <RecentlyViewedDiamondLink
          key={recentlyViewedData[rvCounter + 1].HooverStockId}
          rv={recentlyViewedData[rvCounter + 1]}
        />
        <RecentlyViewedDiamondLink
          key={recentlyViewedData[rvCounter + 2].HooverStockId}
          rv={recentlyViewedData[rvCounter + 2]}
        />
      </>
    );
  }

  function goRVRight() {
    if (rvCounter < recentlyViewedData.length - 3) {
      setRvCounter(rvCounter + 1);
    }
  }

  function goRVLeft() {
    if (rvCounter > 0) {
      setRvCounter(rvCounter - 1);
    }
  }

  function addToCart() {
    if (!terms) {
      setShowAlert(true);
    } else {
      const addToCartBody = {
        cart_id:
          storage.getStorageItem("cartId") === "null" ||
          storage.getStorageItem("cartId") === "undefined"
            ? localStorage.removeItem("cartId")
            : storage.getStorageItem("cartId"),
        partcode: {
          material: "D",
          product: diamond.Shape,
          dimension: `${diamond.Color}${diamond.Clarity}`,
          finger_size: diamond.HooverStockId,
          quantity: "1",
          quantity_unit: "EA",
          carat_size: diamond.Carat.toString(),
          custom_dimension: {
            sale: conditionalSale ? 1 : 0,
            terms: 1,
          },
          message: conditionalSale
            ? `Conditional Sale. ${specialInstructions}`
            : specialInstructions,
        },
      };
      api
        .postRequest("/cart/add", addToCartBody)
        .then((res) => {
          cartDispatch({
            type: "updateCart",
            payload: {
              cart: res.data.items,
              cartCount: res.data.count,
            },
          });
          storage.setStorageItem("cartId", res.data.cart_id);
          setAddedToCart(true);
        })
        .catch((error) => {
          toast.error(
            "Problem occurred when adding to cart. Please try again."
          );
          localStorage.removeItem("cartId");
        });
    }
  }

  const checkboxesAndSpecialInstructions = () => (
    <Stack spacing={1}>
      <FormControlLabel
        control={
          <Checkbox
            checked={conditionalSale}
            onChange={() => setConditionalSale(conditionalSale ? false : true)}
          />
        }
        label={
          <Typography variant="body3" sx={{ color: "firebrick" }}>
            Order for Conditional Sale
            <HelpIcon
              className="help-icon question-mark"
              onClick={() => {
                setShowOFCHelp(true);
              }}
            />
          </Typography>
        }
        sx={{ margin: "unset" }}
      />
      <FormControlLabel
        label={
          <Typography variant="cellFont">
            I have read and agree to the{" "}
            <DomLink
              to={{ pathname: "/links/Company-Policies" }}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Terms & Conditions
            </DomLink>{" "}
            *
          </Typography>
        }
        control={
          <Checkbox
            checked={terms}
            onChange={() => setTerms(terms ? false : true)}
          />
        }
      />
      <TextField
        color="primary"
        variant="outlined"
        size="small"
        value={specialInstructions}
        onChange={(e) => setSpecialInstructions(e.target.value)}
        inputProps={{
          style: {
            paddingBottom: 100,
          },
        }}
        label={<Typography variant="body3">Special Instructions</Typography>}
      />
    </Stack>
  );

  const filteredSpecificationFields = useMemo(() => {
    if (!Array.isArray(fields)) {
      return [];
    }

    const shape = diamond?.Shape ?? "";
    const color = diamond?.Color ?? "";
    const girdle = diamond?.Girdle ?? "";
    const culet = diamond?.Culet ?? "";

    const shouldHideGirdle =
      shape === "Round" || shape === "Heart" || girdle === "";
    const shouldHideCulet =
      shape === "Round" || shape === "Heart" || culet === "";
    const showColorField = color !== "";
    const fancyColorFieldNames = new Set([
      "FancyColor",
      "FancyColorIntensity",
      "FancyColorOvertone",
    ]);

    return fields
      .filter((field) => field.details_order !== null)
      .filter(
        (field) => field.name !== "ImageLink" && field.name !== "VideoLink"
      )
      .filter((field) => {
        if (field.name === "Girdle") {
          return !shouldHideGirdle;
        }

        if (field.name === "Culet") {
          return !shouldHideCulet;
        }

        const fieldValue = resolveFieldValue(field);

        if (field.name === "LWRatio" || field.label === "L/W Ratio") {
          if (
            fieldValue === null ||
            fieldValue === undefined ||
            fieldValue === "" ||
            fieldValue === "-"
          ) {
            return false;
          }
        }

        if (!showColorField && field.name === "Color") {
          return false;
        }

        if (showColorField && fancyColorFieldNames.has(field.name)) {
          return false;
        }

        if (fieldValue === null || fieldValue === undefined) {
          return false;
        }

        return true;
      })
      .sort((a, b) => a.details_order - b.details_order);
  }, [fields, diamond]);

  const renderSpecificationValue = (field) => {
    const rawValue = resolveFieldValue(field);
    const certificationLink = diamond?.CertificationLocation;
    const formattingFn = field.format ? formatting[field.format] : null;
    const formattedValue = formattingFn ? formattingFn(rawValue) : rawValue;
    const displayValue = getDisplayValue(formattedValue);

    if (field.label === "Report" && certificationLink) {
      return displayValue === "None" ? (
        displayValue
      ) : (
        <>
          {displayValue}&nbsp;&nbsp;
          <a
            href={certificationLink}
            className="underline"
            target="_blank"
            rel="noreferrer"
          >
            View
          </a>
        </>
      );
    }

    return displayValue;
  };

  const specificationsBlock = () => (
    <Stack>
      <Typography variant="h5">Specifications:</Typography>
      <Divider />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            sm: "repeat(1, 1fr)",
            md: "repeat(2, 1fr)",
            lg: "repeat(2, 1fr)",
            xl: "repeat(2, 1fr)",
          },
        }}
      >
        <Typography variant="body3" sx={{ padding: ".5rem" }}>
          {formatHeadlineText()}
        </Typography>

        {filteredSpecificationFields.map((field) => (
          <Typography
            key={field.name}
            variant="body3"
            sx={{ padding: ".5rem" }}
          >
            {field.label}: {renderSpecificationValue(field)}
          </Typography>
        ))}
      </Box>
    </Stack>
  );

  return (
    <>
      {!pageLoaded ? (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={true}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : (
        <Container maxWidth="xl">
          {diamondUnavailable ? (
            <Stack paddingTop={"2rem"} alignItems="center">
              <Typography variant="articleTitle" textAlign="center">
                This item is not available at this time
              </Typography>
            </Stack>
          ) : (
            <>
              <Stack paddingTop={"2rem"}>
                <Typography variant="articleTitle">
                  {`${diamond.MinedOrLab === "L" ? lab : mined} Diamond`}
                  <Typography variant="h3" className="sku">
                    SKU: {skuDisplay}
                  </Typography>
                </Typography>
                {diamond.Vendor === "124555" && diamond.MinedOrLab === "L" && (
                  <Typography variant="subtitle1">
                    Ships Same Day <LocalShippingTwoToneIcon />
                  </Typography>
                )}
                <Typography
                  marginTop={"2rem"}
                  variant={isMdScreen ? "h5" : "h6"}
                  textAlign={isMdScreen ? "center" : ""}
                >
                  {buildDetailLine()}
                  {!isMdScreen ? (
                    <Tooltip
                      title="Shareable Page without Pricing"
                      placement="top-start"
                      className="float-right"
                    >
                      <DomLink
                        id="blank-diamond-details"
                        to={{
                          pathname: `/blankdiamond/${diamond.HooverStockId}`,
                        }}
                        target="_blank"
                      >
                        <Chip
                          className="pointer"
                          icon={<OpenInNewSharpIcon />}
                          label="Shareable Page w/o Pricing"
                          color="primary"
                          variant="outlined"
                          sx={{ paddingLeft: ".5rem" }}
                        />
                      </DomLink>
                    </Tooltip>
                  ) : null}
                </Typography>
                <Divider
                  flexItem
                  sx={{ marginBottom: "2rem", marginTop: "2rem" }}
                />
                <Stack
                  direction={isMdScreen ? "column" : "row"}
                  spacing={8}
                  justifyContent="center"
                >
                  <Stack
                    sx={{
                      width: "100%",
                      minHeight: "100%",
                    }}
                  >
                    <MediaPanel
                      diamond={diamond}
                      popover={props.popover}
                      details={true}
                    />
                  </Stack>
                  <Stack spacing={2}>
                    <Stack direction={"row"} spacing={2} alignItems={"center"}>
                      <Typography variant="body3">Source: </Typography>
                      <div style={{ width: "40%" }}>
                        <DiamondIconStrip
                          diamond={diamond}
                          show_text={true}
                          center={false}
                        />
                      </div>
                    </Stack>
                    {loggedIn ? (
                      <>
                        <Stack direction="row" spacing={3}>
                          <Typography variant="body3">
                            {`Price P/C: ${pricePerCaratDisplay}`}
                          </Typography>
                          {diamond.Color !== "" && (
                            <Typography variant="body3">
                              {`% Off Rap: ${percentOffRapDisplay}`}
                            </Typography>
                          )}
                        </Stack>
                        <Divider />
                        <Typography variant="h2" className="center">
                          Total Price: {totalPriceDisplay}
                        </Typography>
                        {checkboxesAndSpecialInstructions()}
                        {addedToCart ? (
                          <Stack
                            direction={isMdScreen ? "column" : "row"}
                            alignItems={"center"}
                            spacing={1}
                          >
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={() => window.close()}
                            >
                              Continue Shopping
                            </Button>
                            <DomLink
                              to={{ pathname: "/cart" }}
                              rel="noreferrer"
                            >
                              <Button variant="contained">Checkout</Button>
                            </DomLink>
                          </Stack>
                        ) : (
                          <Button
                            variant="contained"
                            color="success"
                            onClick={() => addToCart()}
                            sx={{ width: "100%" }}
                          >
                            Add to Cart
                          </Button>
                        )}
                      </>
                    ) : (
                      <DomLink
                        to={{
                          pathname: "/login",
                          state: { sendTo: sendTo.pathname },
                        }}
                      >
                        <Button variant="contained" color="error">
                          Login For Pricing
                        </Button>
                      </DomLink>
                    )}
                  </Stack>
                </Stack>
              </Stack>
              <Grid container rowSpacing={2} justifyContent="center">
                <Grid item className="details-container">
                  <Card variant="outlined" width={"100%"}>
                    <CardContent className="additional-info">
                      {specificationsBlock()}
                    </CardContent>
                  </Card>
                  {recentlyViewedData && (
                    <Grid
                      container
                      className={
                        recentlyViewedData.length > 2 ? "" : "display-none"
                      }
                    >
                      <Grid item xs={12}>
                        <Stack className="recently-viewed-container">
                          <Typography>Recently Viewed</Typography>
                          <Divider flexview="true" sx={{ width: "100%" }} />
                          <div className="rv-strip">
                            <div
                              className={
                                rvCounter > 0
                                  ? "rv-chevrons"
                                  : "rv-chevrons invisible"
                              }
                              onClick={(x) => goRVLeft()}
                            >
                              <ChevronLeftIcon
                                sx={{ verticalAlign: "middle" }}
                              />
                            </div>
                            {rvPics}
                            <div
                              className={
                                rvCounter + 3 < recentlyViewedData.length
                                  ? "rv-chevrons"
                                  : "rv-chevrons invisible"
                              }
                              onClick={(x) => goRVRight()}
                            >
                              <ChevronRightIcon
                                sx={{ verticalAlign: "middle" }}
                              />
                            </div>
                          </div>
                        </Stack>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Modal
                title="Loose Diamond Memo Policy:"
                onClose={() => setShowOFCHelp(false)}
                show={showOFCHelp}
              />
              <Dialog
                open={showAlert}
                onClose={() => setShowAlert(false)}
                onBackdropClick={() => setShowAlert(false)}
              >
                <DialogTitle sx={{ fontSize: "1.6rem", fontWeight: "bold" }}>
                  {`Terms & Conditions`}
                  <IconButton
                    aria-label="close"
                    onClick={() => setShowAlert(false)}
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: 8,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    <Typography gutterBottom sx={{ fontSize: "1.4rem" }}>
                      Please accept the terms and conditions.
                    </Typography>
                  </DialogContentText>
                </DialogContent>
              </Dialog>
            </>
          )}
        </Container>
      )}
    </>
  );
}
